import React, { useContext, useEffect, useState } from 'react'
import { AppSettings } from '../../config/app-settings'
import ReusableTable from '../../components/other/ReuseableTable'
import {
  useGetCampaignsQuery,
  useLazyGetCampaignsQuery
} from '../../redux/apis/campaignsManagement/campaigns/cMCampaignsApiSlice'
import { Link } from 'react-router-dom'
import Loader from '../pages/loader'
import { toast } from 'react-toastify'
import { useNavigate } from 'react-router'
import SearchAndFilterBar from '../../components/other/search/SearchAndFilterBar'
import CustomersDropdown from '../../components/CustomersDropdown'
import DownloadAllDataButton from '../../components/other/DownloadAllDataButton'
import { can } from '../../helpers/auth'
import CampaignInfoModal from '../../components/campaigns/info/CampaignInfoModal'

const CampaignsInfo = React.memo(() => {
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10
  })
  const [search, setSearch] = useState('')
  const [filters, setFilters] = useState({})
  const [infoModalOpen, setInfoModalOpen] = useState(false)
  const [infoCampaignName, setInfoCampaignName] = useState('')
  
  let navigate = useNavigate()
  const context = useContext(AppSettings)

  const { data, isLoading, error, isFetching } = useGetCampaignsQuery({
    expand: [
      'customer_services',
      'customer_services.customer',
      'last_updated_by'
    ].join(','),
    page: pagination.current,
    page_size: pagination.pageSize,
    fields: [
      'id',
      'campaign_name',
      'campaign_release_date',
      'last_updated_by',
      'customer_services',
      'customer_services.customer',
      'customer_services.is_active',
      'customer_services.start_date',
      'customer_services.end_date',
      'crawling_priority'
    ].join(','),
    ...(search ? { search } : {}),
    ...(() => {
      let cleaned = { ...filters }
      if (cleaned.customers) {
        cleaned.customer = cleaned.customers
        delete cleaned.customers
      }
      return cleaned
    })()
  })

  let columns = [
    { title: 'ID', dataIndex: 'id', key: 'id', sorter: true },
    {
      title: 'CAMPAIGN NAME',
      dataIndex: 'campaign_name',
      key: 'campaign_name',
      filter: true,
      filterType: 'text'
    },
    {
      title: 'CUSTOMERS',
      dataIndex: 'customers',
      key: 'customers',
      filter: true,
      filterComponent: CustomersDropdown,
      render: (customers, record) => (
        <>{customers?.map(x => x.customer_name)?.join(', ')}</>
      )
    },
    {
      title: 'CAMPAIGN RELEASE DATE',
      dataIndex: 'campaign_release_date',
      key: 'campaign_release_date',
      filter: true,
      filterType: 'date'
    },
    {
      title: 'CRAWLING PIRORITY',
      dataIndex: 'crawling_priority',
      key: 'crawling_priority',
      render: (value) => {
        const priorityMap = {
          3: 'High',
          2: 'Medium',
          1: 'Low'
        }
        return <>{priorityMap[value] || '-'}</>
      },
      filter: true,
      filterType: 'select',
      filterOptions: [
        { value: 1, label: 'Low' },
        { value: 2, label: 'Medium' },
        { value: 3, label: 'High' },
      ]
    },
    {
      title: 'STATUS',
      dataIndex: 'status',
      key: 'status',
      filter: true,
      filterType: 'select',
      filterOptions: [
        { value: true, label: 'Active' },
        { value: false, label: 'Inactive' }
      ],
      render: (text, record) => <p>{record.status_label ?? (text ? 'Active' : 'Inactive')}</p>
    },
    {
      title: 'LAST UPDATED BY',
      dataIndex: 'last_updated_by',
      key: 'last_updated_by',
      render: text => (
        <p>
          {text?.first_name} {text?.last_name}
        </p>
      )
    },
    {
      title: 'Actions',
      dataIndex: 'id',
      key: 'actions',
      render: (_, record) => (
        <div className='flex gap-2'>
          <button
            className='btn btn-outline-theme'
            onClick={() => {
              setInfoCampaignName(record.campaign_name);
              setInfoModalOpen(true);
            }}
          >
            Info
          </button>
          {can('change_campaign') && (
            <Link to={`/campaigns/campaigns-info/${record?.id}`} className='btn btn-outline-theme'>
              Edit
            </Link>
          )}
        </div>
      ),
    }
  ]

  const handleTableChange = paginationInfo => {
    setPagination({
      current: paginationInfo.current,
      pageSize: paginationInfo.pageSize
    })
  }

  useEffect(() => {
    if (error) {
      toast.error(error)
    }
  }, [error])

  useEffect(() => {
    context.setAppHeaderNone(false)
    context.setAppSidebarNone(false)
    context.setAppContentClass('')
    context.setAppThemePanal(true)

    return () => {
      context.setAppHeaderNone(true)
      context.setAppSidebarNone(true)
      context.setAppContentClass('p-0')
      context.setAppThemePanal(false)
    }
  }, [context])

  return (
    <div className='w-full flex flex-col'>
      <Loader isLoading={isLoading || isFetching} />

      <div className='flex'>
        <SearchAndFilterBar
          columns={columns}
          search={search}
          setSearch={setSearch}
          filters={filters}
          setFilters={setFilters}
          setPagination={setPagination}
        />
        <div className='ml-auto flex gap-4'>
          <DownloadAllDataButton
            lazyQueryHook={useLazyGetCampaignsQuery}
            fileName='campaigns'
            transformData={{
              customer_name: item => item.campaign_rights_owner
            }}
            omitFields={['campaign_rights_owner']}
          />
          {can('add_campaign') && (
            <button
              className='btn btn-outline-theme  mb-4'
              onClick={() => {
                navigate('/campaigns/campaigns-info/0')
              }}
            >
              Create New Campaign
            </button>
          )}
        </div>
      </div>

      <div className='row'>
        <ReusableTable
          columns={columns}
          data={data?.data?.results}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: data?.data?.count,
            showSizeChanger: true
          }}
          onChange={handleTableChange}
        />
      </div>
      <CampaignInfoModal
        open={infoModalOpen}
        onClose={() => setInfoModalOpen(false)}
        campaignName={infoCampaignName}
      />
    </div>
  )
})

export default CampaignsInfo
